/**
 * WebTerminal Component
 *
 * A web-based terminal that connects to HPC clusters via WebSocket SSH proxy.
 * Uses xterm.js for terminal emulation.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import '@xterm/xterm/css/xterm.css'
import Button from '@/components/common/Button'
import {
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline'

interface WebTerminalProps {
  hostname: string
  username: string
  onClose: () => void
  initialCommand?: string  // e.g., qsub -I command
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'error'

export function WebTerminal({ hostname, username, onClose, initialCommand }: WebTerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null)
  const terminalInstance = useRef<Terminal | null>(null)
  const fitAddon = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [error, setError] = useState<string>('')
  const [password, setPassword] = useState('')
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Initialize terminal
  useEffect(() => {
    if (!terminalRef.current || terminalInstance.current) return

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        cursorAccent: '#1e1e1e',
        selectionBackground: '#264f78',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
    })

    fitAddon.current = new FitAddon()
    term.loadAddon(fitAddon.current)
    term.loadAddon(new WebLinksAddon())

    term.open(terminalRef.current)
    fitAddon.current.fit()

    terminalInstance.current = term

    // Welcome message
    term.writeln('\x1b[1;34m=== Imperial HPC Web Terminal ===\x1b[0m')
    term.writeln(`Connecting to: ${username}@${hostname}`)
    term.writeln('')

    // Handle resize
    const handleResize = () => {
      if (fitAddon.current) {
        fitAddon.current.fit()
        // Send resize to backend
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'resize',
            cols: term.cols,
            rows: term.rows,
          }))
        }
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      term.dispose()
      terminalInstance.current = null
    }
  }, [hostname, username])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!password) {
      setError('Please enter your password')
      return
    }

    setShowPasswordPrompt(false)
    setStatus('connecting')
    setError('')

    const term = terminalInstance.current
    if (!term) return

    term.writeln('\x1b[33mConnecting...\x1b[0m')

    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = window.location.hostname
    const wsPort = '8001' // Backend port
    const wsUrl = `${wsProtocol}//${wsHost}:${wsPort}/api/v1/hpc/terminal/ws`

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setStatus('authenticating')
      term.writeln('\x1b[33mAuthenticating...\x1b[0m')

      // Send connection request
      ws.send(JSON.stringify({
        type: 'connect',
        hostname,
        username,
        password,
        cols: term.cols,
        rows: term.rows,
      }))
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'connected':
            setStatus('connected')
            term.writeln('\x1b[32mConnected!\x1b[0m')
            term.writeln('')

            // Send initial command if provided (e.g., qsub -I)
            if (initialCommand) {
              setTimeout(() => {
                ws.send(JSON.stringify({
                  type: 'input',
                  data: initialCommand + '\n',
                }))
              }, 500)
            }
            break

          case 'output':
            term.write(data.data)
            break

          case 'error':
            setStatus('error')
            setError(data.message)
            term.writeln(`\x1b[31mError: ${data.message}\x1b[0m`)
            break

          case 'disconnected':
            setStatus('disconnected')
            term.writeln('\x1b[33mDisconnected from server\x1b[0m')
            break
        }
      } catch {
        // Raw output (not JSON)
        term?.write(event.data)
      }
    }

    ws.onerror = () => {
      setStatus('error')
      setError('WebSocket connection error')
      term.writeln('\x1b[31mConnection error\x1b[0m')
    }

    ws.onclose = () => {
      if (status === 'connected') {
        term.writeln('\x1b[33mConnection closed\x1b[0m')
      }
      setStatus('disconnected')
    }

    // Handle terminal input
    term.onData((data: string) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'input',
          data,
        }))
      }
    })
  }, [hostname, username, password, initialCommand, status])

  // Disconnect
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setStatus('disconnected')
    setShowPasswordPrompt(true)
    setPassword('')
  }, [])

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
    setTimeout(() => {
      fitAddon.current?.fit()
    }, 100)
  }

  return (
    <div className={`flex flex-col bg-gray-900 rounded-lg overflow-hidden ${
      isFullscreen ? 'fixed inset-4 z-50' : 'h-[500px]'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-sm text-gray-300">
            {username}@{hostname}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded ${
            status === 'connected' ? 'bg-green-600 text-white' :
            status === 'connecting' || status === 'authenticating' ? 'bg-yellow-600 text-white' :
            status === 'error' ? 'bg-red-600 text-white' :
            'bg-gray-600 text-gray-300'
          }`}>
            {status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-1 text-gray-400 hover:text-white"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="w-5 h-5" />
            ) : (
              <ArrowsPointingOutIcon className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white"
            title="Close terminal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Password prompt overlay */}
      {showPasswordPrompt && (
        <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center z-10">
          <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-white mb-4">
              SSH Authentication
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Enter your password for {username}@{hostname}
            </p>

            {error && (
              <div className="mb-4 p-2 bg-red-900/50 border border-red-700 rounded text-sm text-red-300">
                {error}
              </div>
            )}

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && connect()}
              placeholder="Password"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
              autoFocus
            />

            {initialCommand && (
              <div className="mb-4 p-2 bg-gray-700 rounded">
                <p className="text-xs text-gray-400 mb-1">Will execute after connection:</p>
                <code className="text-sm text-green-400 break-all">{initialCommand}</code>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={connect} className="flex-1">
                Connect
              </Button>
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Terminal */}
      <div
        ref={terminalRef}
        className="flex-1 p-2"
        style={{ minHeight: 0 }}
      />

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-1 bg-gray-800 border-t border-gray-700 text-xs text-gray-400">
        <span>
          {status === 'connected' ? 'Connected - Press Ctrl+D to disconnect' :
           status === 'error' ? error :
           'Not connected'}
        </span>
        {status === 'connected' && (
          <button
            onClick={disconnect}
            className="text-red-400 hover:text-red-300"
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  )
}

export default WebTerminal
